import {createElement, type ReactElement} from 'react';
import {render, unmountComponentAtNode} from 'react-dom';
import {createRoot} from 'react-dom/client';

import {RenderModeContextProvider} from './RenderModeContext';

/**
 * This horrible piece of work is an attempt to support both render modes that
 * React 18 offers: the old-style non-concurrent mode, and the new concurrent
 * one. To do this, it wraps the render behavior of both modes, and allows for
 * runtime switching between them. The way it does this is ... not good.
 *
 * If the user suffers the switch from one mode to another, the entire app will
 * unmount and remount, probably destroying some in-progress state. Ideally this
 * happens rarely. Fully unmounting and remounting is not well-supported by
 * React, and it will complain if the unmount process doesn't totally destroy
 * the DOM node that it was rendering into.
 *
 * One reason this code is as obtuse as it is, is that we want to continue to
 * support hot module replacment in development. It does that as pragmatically
 * as possible.
 *
 * FIXME: When we are confident that concurrent mode works correctly in the app
 *        this utility should be deleted and forgotten about. ☠️☠️☠️
 */
export class DualModeReactRenderer {
  protected renderMode: 'concurrent' | 'legacy' = 'concurrent';
  private node: HTMLElement;
  private renderFn: (app: any) => ReactElement;
  private app: any;
  private unmountCallback?: () => void;

  constructor(node: HTMLElement, renderFn: (app: any) => ReactElement) {
    this.node = node;
    this.renderFn = renderFn;
  }

  renderTree(app: any) {
    return createElement(
      RenderModeContextProvider,
      {value: this},
      this.renderFn(app)
    );
  }

  changeRenderMode(mode: 'concurrent' | 'legacy') {
    if (this.renderMode === mode) {
      return;
    }

    this.renderMode = mode;
    this.unmount();
    this.rerender();
  }

  private renderConcurrentMode(app: any) {
    const root = createRoot(this.node);
    root.render(this.renderTree(app));

    return () => {
      root.unmount();
    };
  }

  private renderLegacyMode(app: any) {
    render(this.renderTree(app), this.node);
    return () => {
      unmountComponentAtNode(this.node);
    };
  }

  private unmount() {
    if (this.unmountCallback) {
      this.unmountCallback();

      const cl = document.createElement('div');
      this.node.parentNode!.appendChild(cl);
      this.node.parentNode!.removeChild(this.node);
      this.node = cl;
    }
  }

  rerender() {
    // Concurrent is the default (see renderMode). The legacy path is kept as a
    // rollback target driven by the render-mode feature flag. Either branch must
    // store the teardown callback so unmount() works — without it,
    // changeRenderMode() can't tear down the previous tree and HMR re-renders
    // call createRoot()/render() again on an already-rendered node.
    if (this.renderMode === 'concurrent') {
      this.unmountCallback = this.renderConcurrentMode(this.app!);
    } else {
      this.unmountCallback = this.renderLegacyMode(this.app!);
    }
  }

  render(element: any) {
    this.app = element;
    this.rerender();
  }
}
